import { Box, Chip, Grid, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { isEmptyOrUndefined } from '@zippeditoolsjs/blocks';
import { DataGrid } from "@zippeditoolsjs/table";
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import CircularLoader from '../../tools/CircularLoader';
import { contactLevelColor } from '../../utils/colors';

// Column styles
const mediumFlex = 0.5;
const largeFlex = 1;

const columns = (t, theme, onEdit, onDelete) => {
  const columns = [
    {
      field: 'principal',
      headerName: t('cws_app.contacts.Principal', 'Principal'),
      disableColumnMenu: true
    },
    {
      field: 'client_name',
      headerName: t('cws_app.contacts.Client', 'Client'),
      disableColumnMenu: true
    },
    {
      field: 'level',
      headerName: t('cws_app.contacts.Level', 'Level'),
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Tooltip title={t(`cws_app.contacts.${params.row.level}`, params.row.level)}>
            <Box sx={{ px: 1, py: 0.5, borderRadius: '5px', background: contactLevelColor(params.row.level, theme) }}>
              {t(`cws_app.contacts.${params.row.level}`, params.row.level)}
            </Box>
          </Tooltip>
        )
      }
    },
    {
      field: 'contact',
      headerName: t('cws_app.contacts.Contact', 'Contact'),
      disableColumnMenu: true,
      flex: largeFlex,
      valueGetter: (params) => {
        return `${params.row.contact_name} ${params.row.email} ${params.row.phone}`;
      },
      renderCell: (params) => {
        return (
          <Tooltip title={
            <Stack>
              <Box>{params.row.contact_name}</Box>
              <Box>{params.row.email}</Box>
              <Box>{params.row.phone}</Box>
            </Stack>
          }>
            <Stack sx={{ overflow: 'hidden' }}>
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <IconComponent
                  iconName={'person-circle-outline'}
                  style={{ fontSize: '14px' }}
                />
                <Box>{params.row.contact_name ? params.row.contact_name : '-'}</Box>
              </Stack>
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <IconComponent
                  iconName={'mail-outline'}
                  style={{ fontSize: '14px' }}
                />
                <Box>{params.row.email ? params.row.email : '-'}</Box>
              </Stack>
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <IconComponent
                  iconName={'call-outline'}
                  style={{ fontSize: '14px' }}
                />
                <Box>{params.row.phone ? params.row.phone : '-'}</Box>
              </Stack>
            </Stack>
          </Tooltip>
        )
      }
    },
    {
      field: 'user',
      headerName: t('cws_app.general.User', 'User'),
      disableColumnMenu: true,
      flex: largeFlex,
      valueGetter: (params) => {
        return `${params.row.first_name} ${params.row.last_name} ${params.row.cws_email} ${params.row.task_manager_email}`;
      },
      renderCell: (params) => {
        if (params.row.task_manager_email == null && params.row.cws_email == null) {
          return <Typography variant='caption' color='textSecondary'>{t('cws_app.general.No_user_assigned', 'No user assigned')}</Typography>
        }
        return (
          <Stack sx={{ overflow: 'hidden' }}>
            <Tooltip title={
              <Stack>
                <Box>{params.row.first_name} {params.row.last_name}</Box>
                {params.row.cws_email === params.row.task_manager_email ?
                  <Box>{params.row.cws_email}</Box>
                  :
                  <Stack>
                    <Box>{params.row.cws_email ? params.row.cws_email : '-'}</Box>
                    <Box>{params.row.task_manager_email ? params.row.task_manager_email : '-'}</Box>
                  </Stack>
                }
              </Stack>
            }>
              <p>
                {params.row.first_name} {params.row.last_name}
              </p>
            </Tooltip>
            {params.row.cws_email === params.row.task_manager_email ?
              <Stack direction='row' spacing={0.3} alignItems='center'>
                <Tooltip title={'Task Manager App'}>
                  <p>
                    <IconComponent
                      iconName={'phone-portrait-outline'}
                      style={{ fontSize: '14px', color: 'gray' }}
                    />
                  </p>
                </Tooltip>
                <Tooltip title={'Clients Website'}>
                  <p>
                    <IconComponent
                      iconName={'laptop-outline'}
                      style={{ fontSize: '14px', color: 'gray' }}
                    />
                  </p>
                </Tooltip>
                <Typography variant='caption' color='textSecondary'>{params.row.task_manager_email}</Typography>
              </Stack>
              :
              <Stack>
                <Stack direction='row' spacing={0.3} alignItems='center'>
                  <Tooltip title={'Task Manager App'}>
                    <p>
                      <IconComponent
                        iconName={'phone-portrait-outline'}
                        style={{ fontSize: '14px', color: 'gray' }}
                      />
                    </p>
                  </Tooltip>
                  <Typography variant='caption' color='textSecondary'>{params.row.cws_email ? params.row.cws_email : '-'}</Typography>
                </Stack>
                <Stack direction='row' spacing={0.3} alignItems='center'>
                  <Tooltip title={'Clients Website'}>
                    <p>
                      <IconComponent
                        iconName={'laptop-outline'}
                        style={{ fontSize: '14px', color: 'gray' }}
                      />
                    </p>
                  </Tooltip>
                  <Typography variant='caption' color='textSecondary'>{params.row.task_manager_email ? params.row.task_manager_email : '-'}</Typography>
                </Stack>
              </Stack>
            }
          </Stack>
        )
      }
    },
    {
      field: 'contact_role',
      headerName: t('cws_app.contacts.form.contact_role', 'Contact Role'),
      disableColumnMenu: true,
      renderCell: (params) => {
        const role = params.row.contact_role;
        return role ? role : '-';
      }
    },
    {
      field: 'task_manager_roles',
      headerName: t('cws_app.contacts.Task_manager_roles', 'Task manager roles'),
      disableColumnMenu: true,
      sortable: false,
      flex: largeFlex,
      valueGetter: (params) => {
        const names = params.row.task_manager_roles.map(role => role.role_name);
        return names.join(', ')
      },
      renderCell: (params) => {
        return (
          <Tooltip title={
            <>
              {params.row.task_manager_roles.map(role => (
                <Box key={role.role_id}>
                  {t(`cws_app.contacts.${role.role_name}`, role.role_name)}
                </Box>
              ))}
            </>
          }>
            {params.row.task_manager_roles.length > 0 ?
              <Grid container spacing={1} sx={{ overflow: 'hidden' }}>
                {params.row.task_manager_roles.map(role => (
                  <Grid item key={role.role_id}>
                    <Chip size="small" variant='outlined' label={t(`cws_app.contacts.${role.role_name}`, role.role_name)} color={'primary'} />
                  </Grid>
                ))}
              </Grid>
              :
              <>-</>
            }
          </Tooltip>
        )
      }
    },
    {
      field: 'notification_features',
      headerName: t('cws_app.contacts.Notification_features', 'Notification Features'),
      disableColumnMenu: true,
      sortable: false,
      valueGetter: (params) => {
        const names = params.row.notification_features.map(feature => feature.notification_feature_name);
        return names.join(', ')
      },
      renderCell: (params) => {
        return (
          <Tooltip title={
            <>
              {params.row.notification_features.map(feature => (
                <Box key={feature.notification_feature_id}>
                  {t(`cws_app.contacts.${feature.notification_feature_name}`, feature.notification_feature_name)}
                </Box>
              ))}
            </>
          }>
            <Box sx={{ px: 1, py: 0.5, borderRadius: '5px', background: theme.palette.grey[300] }}>
              {params.row.notification_features_coverage}
            </Box>
          </Tooltip>
        )
      }
    },
    {
      field: 'cws_user_clients_products_coverage',
      headerName: t('cws_app.contacts.CWS_Products', 'CWS Products'),
      disableColumnMenu: true,
      sortable: false,
      flex: mediumFlex,
      valueGetter: (params) => {
        const names = params.row.cws_user_clients_products
          .filter(product => product.is_active)
          .map(product => product.product_name);
        return names.join(', ')
      },
      renderCell: (params) => {
        const activeProducts = params.row.cws_user_clients_products.filter(
          (product) => product.is_active
        );

        const cellContent = (
          <Box sx={{ px: 1, py: 0.5, borderRadius: '5px', background: theme.palette.grey[300] }}>
            {params.row.cws_user_clients_products_coverage}
          </Box>
        );

        if (activeProducts.length === 0) {
          return cellContent;
        }
        return (
          <Tooltip title={
            <>
              {activeProducts.map((product) => (
                <Box key={product.product_id}>
                  {t(`cws_app.general.${product.product_name}`, product.product_name)}
                </Box>
              ))}
            </>
          }>
            {cellContent}
          </Tooltip>
        )
      }
    },
    {
      field: 'actions',
      headerName: t('cws_app.general.actions', 'Actions'),
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack direction="row">
          <IconButton onClick={() => onEdit(params.row)} color="primary">
            <IconComponent iconName="pencil" />
          </IconButton>
          <IconButton onClick={() => onDelete(params.row)} color="error">
            <IconComponent iconName="trash" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return columns;
}

export default function ContactsTable(props) {
  const {
    t,
    theme,
    contacts,
    isLoadingContacts,
    onEdit,
    onDelete,
  } = props;

  return (
    <>
      {isLoadingContacts ?
        <Grid container justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
          <CircularLoader />
        </Grid>
        :
        !isEmptyOrUndefined(contacts, 'array') ?
          <Paper sx={{ px: 2, py: 1, justifyContent: 'center', alignItems: 'center', display: 'flex', width: '100%', backgroundColor: theme.palette.paper.main, overflow: 'hidden' }}>
            <DataGrid
              sx={{
                border: 'none',
                width: '100%',
              }}
              rows={contacts}
              columns={columns(t, theme, onEdit, onDelete)}
              getRowId={(row) => row.contact_id}
              getRowHeight={() => 'auto'}
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              // Used to hide columns by default, 
              // while still allowing them to be used for filtering
              columnVisibilityModel={{
                client_name: false,
              }}
              pageSizeOptions={[5, 10, 15]}
              disableExportExcel={false}
              localeText={{
                toolbarExport: t('cws_app.general.export', 'Export'),
              }}
              exportButtonTranslations={{
                'excel': t('cws_app.general.export_xls', 'Export Excel'),
              }}
              excelFileName={t('cws_app.contacts.contacts', 'contacts')}
              exportColumns={[
                {
                  field: "client_name",
                  headerName: t('cws_app.contacts.client_name', 'Client')
                },
                "principal",
                {
                  field: "first_name",
                  headerName: t('cws_app.contacts.first_name', 'First Name')
                },
                {
                  field: "last_name",
                  headerName: t('cws_app.contacts.last_name', 'Last Name')
                },
                "contact_role",
                {
                  field: "phone",
                  headerName: t('cws_app.contacts.Phone', 'Phone')
                },
                {
                  field: "email",
                  headerName: t('cws_app.contacts.Email', 'Email')
                }
              ]}
            />
          </Paper>
          :
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography textAlign={'center'} > {t('cws_app.general.No_data', 'No data to display.')}</Typography>
            </Paper>
          </Grid>
      }
    </>
  )
}
